import {
  Text,
  Card,
  Box,
  Flex,
  Avatar,
  ActionIcon,
  Grid,
  Stack,
} from "@mantine/core"
import { UseFormReturnType } from "@mantine/form"
import { useHover } from "@mantine/hooks"
import { IconPlus, IconSearch } from "@tabler/icons-react"
import { UseQueryResult } from "@tanstack/react-query"
import _ from "lodash"
import { ReactNode, useMemo, useCallback } from "react"

import { Filters } from "@costory/types/filters"
import { ExplorerChartType } from "@costory/types/prisma-client"

import { DEFAULT_DATE_FORMAT } from "@costory/shared/const"
import dayjs from "@costory/shared/dayjs"
import { calculateRelativeChange } from "@costory/shared/utils/format"

import { RechartsAreaChart } from "@costory/front/components/charts/RechartsAreaChart"
import { QueryWrapper } from "@costory/front/components/layout/QueryWrapper"
import { useChartActions } from "@costory/front/pages/DigestV2/components/SuggestedCards/useChartActions"
import { PageType } from "@costory/front/pages/DigestV2/components/types"

type ChartData = {
  totals: Array<{ agg_date: string; cost: number }>
}

type MetricCardProps = {
  icon: ReactNode
  title: string
  onAnalyze?: () => void
  chartQuery?: UseQueryResult<ChartData>
  fallback?: ReactNode
  metricId?: string
  initialValues?: Filters
  form?: UseFormReturnType<Filters>
  setChartType?: (type: ExplorerChartType) => void
  setPage?: (page: PageType) => void
  setEditContent: React.Dispatch<React.SetStateAction<string>>
  editContent: string
  isNotOtherNode?: boolean
  setLoading?: (loading: boolean) => void
}

export function MetricCard({
  icon,
  title,
  chartQuery,
  fallback,
  metricId,
  initialValues,
  form,
  setChartType,
  setPage,
  setEditContent,
  editContent,
  isNotOtherNode = true,
  setLoading,
}: MetricCardProps) {
  // Memoize chart data processing
  const chartData = useMemo(() => {
    if (!chartQuery?.data?.totals) return null
    return convertChartData(chartQuery?.data)
  }, [chartQuery?.data])

  const {
    addExplorerImageToNote,
    createChartUrl,
    isChartAdded,
    handleQuickAdd,
    handleAnalyze,
  } = useChartActions({
    form,
    setChartType,
    setPage,
    setEditContent,
    editContent,
    setLoading,
  })

  const chartUrl = useMemo(() => {
    if (!initialValues || !metricId) return ""
    const chartFilters = { ...initialValues, metricId }
    return createChartUrl(chartFilters, ExplorerChartType.LINE)
  }, [initialValues, metricId, createChartUrl])

  const handleQuickAddClick = useCallback(
    async (e: React.MouseEvent) => {
      if (!initialValues || !metricId) return

      const chartFilters = { ...initialValues, metricId, groupBy: "" }
      await handleQuickAdd(e, chartFilters, ExplorerChartType.LINE)
    },
    [initialValues, metricId, handleQuickAdd],
  )

  const handleAnalyzeClick = useCallback(() => {
    if (!initialValues || !metricId || !form || !setChartType || !setPage)
      return
    const chartFilters = { ...initialValues, metricId, groupBy: "" }
    handleAnalyze(chartFilters, ExplorerChartType.LINE)
  }, [initialValues, metricId, form, setChartType, setPage, handleAnalyze])

  const { ref, hovered } = useHover()

  return (
    <Card
      ref={ref}
      withBorder={false}
      radius="md"
      p={5}
      h="100%"
      style={{ display: "flex", flexDirection: "column" }}
      onClick={handleAnalyzeClick}
      styles={{
        root: {
          cursor: "pointer",
        },
      }}
    >
      <Stack gap={4} style={{ flexGrow: 1 }}>
        <Grid align="center" gutter="xs">
          <Grid.Col span="auto">
            <Flex align="center" gap="xs" wrap="wrap">
              {icon}
              <Text size="md" fw={600} style={{ minWidth: 0, flex: 1 }}>
                {title}
              </Text>
              {chartData && chartData.relativeChange !== "0%" && (
                <Flex align="center" gap={4} wrap="wrap">
                  <Avatar
                    radius="xl"
                    size={16}
                    color={chartData.color}
                    variant="light"
                  >
                    {chartData.icon}
                  </Avatar>
                  <Text
                    size="sm"
                    fw={500}
                    c={chartData.color}
                    style={{ whiteSpace: "nowrap" }}
                  >
                    {chartData.formattedDiff}
                  </Text>
                </Flex>
              )}
            </Flex>
          </Grid.Col>
          <Grid.Col span="content">
            {chartData && (
              <Flex gap={4}>
                <ActionIcon
                  size={20}
                  variant="light"
                  color="gray"
                  onClick={handleQuickAddClick}
                  style={{ flexShrink: 0 }}
                  loading={addExplorerImageToNote.isPending}
                  disabled={!isNotOtherNode || isChartAdded(chartUrl)}
                  opacity={!isNotOtherNode || isChartAdded(chartUrl) ? 0.5 : 1}
                  data-intercom-target="costory-tour-digest-metrics-card-add"
                >
                  <IconPlus size={11} />
                </ActionIcon>
                <ActionIcon
                  size={20}
                  variant="light"
                  color="gray"
                  onClick={(e) => {
                    e.stopPropagation()
                    handleAnalyzeClick()
                  }}
                  data-intercom-target="costory-tour-digest-metrics-card-analyze"
                >
                  <IconSearch size={11} />
                </ActionIcon>
              </Flex>
            )}
          </Grid.Col>
        </Grid>
      </Stack>

      {chartQuery ? (
        <QueryWrapper query={chartQuery}>
          {({ data }) => (
            <>
              <RenderChart
                chartData={data}
                handleAnalyzeClick={handleAnalyzeClick}
              />
              {hovered && (
                <Box
                  style={{
                    position: "absolute",
                    top: "20%",
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: "rgba(0, 0, 0, 0.1)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    zIndex: 1,
                    borderRadius: "md",
                  }}
                >
                  <Text c="textSecondary" fw={700}>
                    Click to Explore
                  </Text>
                </Box>
              )}
            </>
          )}
        </QueryWrapper>
      ) : (
        fallback
      )}
    </Card>
  )
}

const convertChartData = (chartData: ChartData) => {
  const sortedTotalsData = _.orderBy(
    chartData.totals,
    (item) => dayjs(item.agg_date).toDate(),
    "asc",
  )
  if (sortedTotalsData.length < 2) return null
  const currentCost = sortedTotalsData[sortedTotalsData.length - 1].cost
  const previousCost = sortedTotalsData[0].cost
  const diff = currentCost - previousCost

  const { relativeChange, formattedDiff, color, icon } =
    calculateRelativeChange(diff, previousCost, currentCost)

  return {
    sortedTotalsData,
    relativeChange,
    formattedDiff,
    color,
    icon,
  }
}

const RenderChart = ({
  chartData,
  handleAnalyzeClick,
}: {
  chartData: ChartData
  handleAnalyzeClick: () => void
}): JSX.Element | null => {
  if (!chartData) return null
  const transformed = convertChartData(chartData)
  if (!transformed) return <Text>No data</Text>
  const { sortedTotalsData, color } = transformed

  return (
    <Box
      style={{ flexGrow: 1, minHeight: "80px" }}
      onClick={handleAnalyzeClick}
    >
      <RechartsAreaChart
        data={sortedTotalsData}
        color={color}
        onClick={handleAnalyzeClick}
        xAxisProps={{
          ticks: [
            sortedTotalsData[0]?.agg_date,
            sortedTotalsData[sortedTotalsData.length - 1]?.agg_date,
          ],
          tickFormatter: (value: string) =>
            dayjs(value).format(DEFAULT_DATE_FORMAT),
          fontSize: 12,
        }}
      />
    </Box>
  )
}
